/**
 * COP-148 D8 — read-only execution visibility for the admin approvals queue.
 *
 * The Execution badge and the Execution filter options live in their own module
 * (NOT inside `./List`) so a `renderToStaticMarkup` unit test can import them
 * without transitively pulling `@/lib/hub-client`, which constructs a HubClient
 * at module load and throws `HubClient: baseUrl not provided` in the test
 * environment. Same precedent as `pages/schedules/created-in-chat-badge.tsx`.
 *
 * Visibility ONLY. COP-085's read-only stance on this surface is extended, not
 * amended: this module renders state and offers no action.
 */
import type { ApprovalExecutionStatus } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';

/** `'all'` is the "no filter" sentinel — it is never sent to the hub. */
export type ExecutionFilter = ApprovalExecutionStatus | 'all';

/**
 * The full option list of the Execution filter select. All five enum members of
 * `tf_approval_execution_status` plus the All escape hatch, in lifecycle order.
 */
export const EXECUTION_OPTIONS: { value: ExecutionFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'executing', label: 'Executing' },
  { value: 'executed', label: 'Executed' },
  { value: 'failed', label: 'Failed' },
  { value: 'unknown', label: 'Unknown' },
];

const EXECUTION_VARIANT: Record<
  ApprovalExecutionStatus,
  'default' | 'success' | 'destructive' | 'outline' | 'muted'
> = {
  pending: 'muted',
  executing: 'default',
  executed: 'success',
  failed: 'destructive',
  // COP-138 — indeterminate, not a confirmed failure. Neutral, not red.
  unknown: 'outline',
};

/**
 * Renders the execution state of one approval row. `executionError` is surfaced
 * as the badge title so an operator can read the failure cause without opening
 * the Show page.
 */
export function ExecutionBadge({
  executionStatus,
  executionError,
}: {
  executionStatus: ApprovalExecutionStatus;
  executionError?: string | null;
}) {
  return (
    <Badge
      variant={EXECUTION_VARIANT[executionStatus]}
      data-testid="approval-execution-badge"
      title={executionError ?? undefined}
    >
      {executionStatus}
    </Badge>
  );
}
